import { useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAgentsPaginatedByAgentId } from '@atlassian/conversation-assistant-agent/controllers/agents';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type Conversation,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { findLast } from '../../controllers/chat-store/findLast';

type UseStreamingThinkingAgentArgs = {
	conversation?: Conversation;
	conversationMessages: StoreChatItem[];
};

export const useStreamingThinkingAgent = ({
	conversation,
	conversationMessages,
}: UseStreamingThinkingAgentArgs): {
	thinkingAgent?: Agent;
	isThinkingAgentHydrating: boolean;
} => {
	di(fg, useAgentsPaginatedByAgentId, useAssistanceServiceParams);

	// Per-turn agent for the "{agent} is thinking" indicator (text + avatar).
	//
	// The thinking agent is the author of the currently streaming assistant
	// message. The optimistic assistant message is authored with the same agent id
	// the turn was routed to, so the indicator does not need to know whether that
	// author came from the conversation agent or a per-turn override.
	const thinkingAuthorAgentId = useMemo(() => {
		if (!isExperimentEnabled('rovo_chat_mention_agents')) {
			return undefined;
		}
		if (conversation?.lastAgentMessageStatus !== 'IN_PROGRESS') {
			return undefined;
		}
		// Use findLast (not getLastMessageOfType) to avoid allocating an intermediate
		// array on every streaming chunk in this hot-path memo.
		const lastAssistantMessage = findLast(
			conversationMessages,
			(item): item is StoreAssistant => item.type === 'ASSISTANT',
		)?.content.message.message;
		return lastAssistantMessage?.agent_author?.id ?? lastAssistantMessage?.author?.named_id;
	}, [conversation?.lastAgentMessageStatus, conversationMessages]);

	const thinkingFallbackServiceParams = useAssistanceServiceParams({
		touchpointSource: 'chatHistoryThinkingAgent',
	});
	const { agent: thinkingAgent } = useAgentsPaginatedByAgentId({
		agentId: thinkingAuthorAgentId,
		assistanceServiceParams: thinkingFallbackServiceParams,
	});

	return {
		thinkingAgent,
		isThinkingAgentHydrating: !!thinkingAuthorAgentId && !thinkingAgent,
	};
};
